import { useState, useRef, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, ListMusic, X, Trash2, Shuffle, Repeat, Repeat1, Mic2, Disc3, Settings, ArrowLeft, Minimize2, Heart, GripVertical, ChevronUp, ChevronDown, ThumbsUp, ThumbsDown, Cast } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import LyricsPanel from './LyricsPanel'
import { usePlayerStore } from '../../stores/player'
import { useVideoPlayerStore, QUALITY_OPTIONS } from '../../stores/videoPlayer'
import { useSettingsStore } from '../../stores/settings'
import { tracks, remotePlayers } from '../../api/client'

function formatTime(seconds: number): string {
  // Handle invalid values
  if (!isFinite(seconds) || isNaN(seconds) || seconds < 0) {
    return '0:00'
  }
  const hrs = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)
  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

function formatDuration(ms: number | null): string {
  if (!ms) return '--:--'
  const seconds = Math.floor(ms / 1000)
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

function getImageUrl(path: string | null): string | null {
  if (!path) return null
  return `/api/image/${encodeURIComponent(path)}`
}

export default function PlayerBar() {
  const videoRef = useRef<HTMLVideoElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const controlsTimeoutRef = useRef<number | null>(null)

  const [showQueue, setShowQueue] = useState(false)
  const [showLyrics, setShowLyrics] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [showCast, setShowCast] = useState(false)
  const [showVideoQuality, setShowVideoQuality] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [isAudioDrawerOpen, setIsAudioDrawerOpen] = useState(false)
  const [showControls, setShowControls] = useState(true)
  const [draggedQueueIndex, setDraggedQueueIndex] = useState<number | null>(null)
  const [dragOverQueueIndex, setDragOverQueueIndex] = useState<number | null>(null)

  const savePositionIntervalRef = useRef<number | null>(null)
  const hasRestoredPosition = useRef(false)
  const remoteVolumeTimerRef = useRef<number | null>(null)
  const remoteVolumeSettlingRef = useRef<number | null>(null)
  const remoteSeekTimerRef = useRef<number | null>(null)
  const remoteSeekSettlingRef = useRef<number | null>(null)
  const remotePausePreviewTimerRef = useRef<number | null>(null)
  const [volumePreview, setVolumePreview] = useState<number | null>(null)
  const [seekPreviewPercent, setSeekPreviewPercent] = useState<number | null>(null)
  const [remotePausedPreview, setRemotePausedPreview] = useState<boolean | null>(null)

  const { skipDownvoted, setSkipDownvoted } = useSettingsStore()

  // Audio player state
  const {
    currentTrack,
    isPlaying: audioIsPlaying,
    progress: audioProgress,
    duration: audioDuration,
    volume: audioVolume,
    isMuted: audioIsMuted,
    queue,
    queueIndex,
    shuffleEnabled,
    repeatMode,
    pause: audioPause,
    resume: audioResume,
    next,
    previous,
    seek: audioSeek,
    setVolume: audioSetVolume,
    toggleMute: audioToggleMute,
    removeFromQueue,
    reorderQueue,
    playFromQueue,
    clearQueue,
    toggleShuffle,
    cycleRepeatMode,
    remotePlayerId,
    remoteStatus,
    setRemotePlayer,
    refreshRemoteStatus,
  } = usePlayerStore()

  // Video player state
  const {
    currentVideo,
    isPlaying: videoIsPlaying,
    progress: videoProgress,
    duration: videoDuration,
    currentTime: videoCurrentTime,
    buffered: videoBuffered,
    volume: videoVolume,
    isMuted: videoIsMuted,
    quality: videoQuality,
    pause: videoPause,
    resume: videoResume,
    setVolume: videoSetVolume,
    toggleMute: videoToggleMute,
    setVideoRef,
    updateProgress,
    setIsPlaying,
    stopVideo,
    setQuality: setVideoQuality,
  } = useVideoPlayerStore()

  // Determine if we're in video mode or audio mode
  const isVideoMode = !!currentVideo
  const isRemoteMode = !!remotePlayerId
  const remoteStatusData = remoteStatus?.status
  const effectiveRemotePaused = remotePausedPreview ?? remoteStatusData?.paused
  const remoteVolume = typeof remoteStatusData?.volume === 'number' ? remoteStatusData.volume : audioVolume
  const isPlaying = isRemoteMode
    ? !!remoteStatusData?.playing && !effectiveRemotePaused
    : isVideoMode ? videoIsPlaying : audioIsPlaying
  const progress = seekPreviewPercent ?? (isRemoteMode
    ? (() => {
        const position = remoteStatusData?.position_seconds ?? 0
        const remoteDuration = remoteStatusData?.duration_seconds ?? 0
        return remoteDuration > 0 ? Math.min(100, Math.max(0, (position / remoteDuration) * 100)) : audioProgress
      })()
    : isVideoMode ? videoProgress : audioProgress)
  const duration = isRemoteMode
    ? remoteStatusData?.duration_seconds ?? audioDuration
    : isVideoMode ? videoDuration : audioDuration
  const volume = volumePreview ?? (isRemoteMode ? remoteVolume : isVideoMode ? videoVolume : audioVolume)
  const canSeek = isRemoteMode ? duration > 0 : true

  // Like functionality
  const queryClient = useQueryClient()

  const { data: availableRemotePlayers = [] } = useQuery({
    queryKey: ['remote-players'],
    queryFn: remotePlayers.list,
    refetchInterval: 5000,
  })

  useEffect(() => {
    refreshRemoteStatus().catch(() => {})
  }, [remotePlayerId, refreshRemoteStatus])

  useEffect(() => {
    if (!remotePlayerId) return
    const timer = window.setInterval(() => {
      refreshRemoteStatus().catch(() => {})
    }, 2000)
    return () => window.clearInterval(timer)
  }, [remotePlayerId, refreshRemoteStatus])

  useEffect(() => {
    return () => {
      if (remoteVolumeTimerRef.current) window.clearTimeout(remoteVolumeTimerRef.current)
      if (remoteVolumeSettlingRef.current) window.clearTimeout(remoteVolumeSettlingRef.current)
      if (remoteSeekTimerRef.current) window.clearTimeout(remoteSeekTimerRef.current)
      if (remoteSeekSettlingRef.current) window.clearTimeout(remoteSeekSettlingRef.current)
      if (remotePausePreviewTimerRef.current) window.clearTimeout(remotePausePreviewTimerRef.current)
    }
  }, [])

  useEffect(() => {
    if (remotePausedPreview === null || remoteStatusData?.paused !== remotePausedPreview) return
    if (remotePausePreviewTimerRef.current) window.clearTimeout(remotePausePreviewTimerRef.current)
    // Keep the optimistic state visible briefly after the Pi confirms it. This
    // prevents stale heartbeat/list responses from making the button bounce
    // between play and pause while the remote command is settling.
    remotePausePreviewTimerRef.current = window.setTimeout(() => setRemotePausedPreview(null), 1200)
  }, [remoteStatusData?.paused, remotePausedPreview])

  // Get like status for current track
  const { data: likeStatus } = useQuery({
    queryKey: ['track-like', currentTrack?.id],
    queryFn: () => currentTrack ? tracks.getLikeStatus(currentTrack.id) : Promise.resolve({ liked: null }),
    enabled: !!currentTrack && !isVideoMode,
  })

  // Toggle like mutation
  const likeMutation = useMutation({
    mutationFn: async () => {
      if (!currentTrack) return
      // Toggle: null -> true (like), true -> null (unlike), false -> true (change dislike to like)
      const newLiked = likeStatus?.liked === true ? null : true
      await tracks.setLike(currentTrack.id, newLiked)
      return newLiked
    },
    onSuccess: (newLiked) => {
      if (!currentTrack) return
      // Update the cache immediately
      queryClient.setQueryData(['track-like', currentTrack.id], { liked: newLiked })
      // Invalidate liked tracks lists to refresh them
      queryClient.invalidateQueries({ queryKey: ['liked-tracks'] })
      queryClient.invalidateQueries({ queryKey: ['tracks', 'liked'] })
    },
  })

  const isLiked = likeStatus?.liked === true
  const isDisliked = likeStatus?.liked === false
  const repeatTitle = repeatMode === 'off' ? 'Repeat off' : repeatMode === 'all' ? 'Repeat all' : 'Repeat one'
  const RepeatIcon = repeatMode === 'one' ? Repeat1 : Repeat

  const setRatingMutation = useMutation({
    mutationFn: async (liked: boolean) => {
      if (!currentTrack) return null
      const newLiked = likeStatus?.liked === liked ? null : liked
      await tracks.setLike(currentTrack.id, newLiked)
      return newLiked
    },
    onSuccess: (newLiked) => {
      if (!currentTrack) return
      queryClient.setQueryData(['track-like', currentTrack.id], { liked: newLiked })
      queryClient.invalidateQueries({ queryKey: ['liked-tracks'] })
      queryClient.invalidateQueries({ queryKey: ['tracks', 'liked'] })
    },
  })

  useEffect(() => {
    if (isVideoMode || !currentTrack) {
      setIsAudioDrawerOpen(false)
    }
  }, [isVideoMode, currentTrack])

  useEffect(() => {
    if (!isAudioDrawerOpen) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [isAudioDrawerOpen])

  // Set up video element ref when it becomes available
  useEffect(() => {
    if (videoRef.current && isVideoMode) {
      setVideoRef(videoRef.current)
    }
    return () => {
      if (!isVideoMode) {
        setVideoRef(null)
      }
    }
  }, [setVideoRef, isVideoMode])

  // Video element event handlers
  useEffect(() => {
    const video = videoRef.current
    if (!video || !isVideoMode) return

    const handleTimeUpdate = () => {
      let bufferedEnd = 0
      if (video.buffered.length > 0) {
        for (let i = 0; i < video.buffered.length; i++) {
          if (video.buffered.start(i) <= video.currentTime) {
            bufferedEnd = Math.max(bufferedEnd, video.buffered.end(i))
          }
        }
      }
      // For transcoded streams, video.duration is often unreliable (Infinity or small buffer size)
      // Prefer the metadata duration (durationMs) when available
      const metadataDuration = currentVideo?.durationMs ? currentVideo.durationMs / 1000 : 0
      const videoDurationValid = video.duration && isFinite(video.duration) && video.duration > 60
      const effectiveDuration = metadataDuration > 0 ? metadataDuration : (videoDurationValid ? video.duration : 0)

      // Extract start offset from URL for transcoded streams (after seeking)
      let startOffset = 0
      const srcUrl = video.src
      const startMatch = srcUrl.match(/[&?]start=([0-9.]+)/)
      if (startMatch) {
        startOffset = parseFloat(startMatch[1])
      }

      // Actual time is video's current time plus any start offset from seeking
      const actualTime = video.currentTime + startOffset
      updateProgress(actualTime, effectiveDuration, bufferedEnd + startOffset)
    }

    const handlePlay = () => setIsPlaying(true)
    const handlePause = () => setIsPlaying(false)
    const handleEnded = async () => {
      setIsPlaying(false)

      if (currentVideo?.id) {
        try {
          const token = localStorage.getItem('token')
          if (token) {
            await fetch(`/api/videos/${currentVideo.id}/position`, {
              method: 'DELETE',
              headers: { 'Authorization': `Bearer ${token}` }
            })
          }
        } catch (error) {
          console.error('Failed to clear playback position:', error)
        }
      }

      stopVideo()
    }

    video.addEventListener('timeupdate', handleTimeUpdate)
    video.addEventListener('play', handlePlay)
    video.addEventListener('pause', handlePause)
    video.addEventListener('ended', handleEnded)

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate)
      video.removeEventListener('play', handlePlay)
      video.removeEventListener('pause', handlePause)
      video.removeEventListener('ended', handleEnded)
    }
  }, [updateProgress, setIsPlaying, stopVideo, isVideoMode, currentVideo])

  // Load saved video playback position
  useEffect(() => {
    const video = videoRef.current
    if (!video || !currentVideo?.id || hasRestoredPosition.current) return

    const loadPosition = async () => {
      try {
        const token = localStorage.getItem('token')
        if (!token) return

        const response = await fetch(`/api/videos/${currentVideo.id}/position`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })

        if (response.ok) {
          const data = await response.json()
          const positionSeconds = data.position_ms / 1000
          const durationSeconds = data.duration_ms ? data.duration_ms / 1000 : 0

          if (durationSeconds > 0 && positionSeconds > 10 && positionSeconds < durationSeconds - 30) {
            video.currentTime = positionSeconds
            hasRestoredPosition.current = true
          }
        }
      } catch (error) {
        console.error('Failed to load playback position:', error)
      }
    }

    if (video.readyState >= 1) {
      loadPosition()
    } else {
      video.addEventListener('loadedmetadata', loadPosition, { once: true })
      return () => video.removeEventListener('loadedmetadata', loadPosition)
    }
  }, [currentVideo?.id])

  // Save video playback position periodically
  useEffect(() => {
    const video = videoRef.current
    if (!video || !currentVideo?.id) return

    const savePosition = async () => {
      if (!video || video.paused || !currentVideo?.id) return

      const positionMs = Math.floor(video.currentTime * 1000)
      const durationMs = Math.floor((currentVideo.durationMs || video.duration * 1000))

      if (positionMs < 5000) return

      try {
        const token = localStorage.getItem('token')
        if (!token) return

        await fetch('/api/videos/positions', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            video_id: currentVideo.id,
            position_ms: positionMs,
            duration_ms: durationMs || null
          })
        })
      } catch (error) {
        console.error('Failed to save playback position:', error)
      }
    }

    savePositionIntervalRef.current = window.setInterval(savePosition, 10000)

    return () => {
      if (savePositionIntervalRef.current) {
        clearInterval(savePositionIntervalRef.current)
      }
      savePosition()
    }
  }, [currentVideo?.id, currentVideo?.durationMs])

  // Reset hasRestoredPosition when video changes
  useEffect(() => {
    hasRestoredPosition.current = false
  }, [currentVideo?.id])

  // Load video when currentVideo changes or video element becomes available
  useEffect(() => {
    const video = videoRef.current
    if (video && currentVideo && isVideoMode) {
      // Add quality parameter to the stream URL
      const baseUrl = currentVideo.streamUrl.split('&quality=')[0]
      const urlWithQuality = `${baseUrl}&quality=${videoQuality}`
      video.src = urlWithQuality
      video.volume = videoVolume
      video.play().catch(console.error)
    }
  }, [currentVideo, videoVolume, isVideoMode, videoQuality])

  // Keyboard shortcuts for expanded mode
  useEffect(() => {
    if (!isExpanded || !isVideoMode) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement) return

      switch (e.key) {
        case ' ':
        case 'k':
          e.preventDefault()
          videoIsPlaying ? videoPause() : videoResume()
          break
        case 'ArrowLeft':
          e.preventDefault()
          skipBackward()
          break
        case 'ArrowRight':
          e.preventDefault()
          skipForward()
          break
        case 'ArrowUp':
          e.preventDefault()
          videoSetVolume(Math.min(1, videoVolume + 0.1))
          break
        case 'ArrowDown':
          e.preventDefault()
          videoSetVolume(Math.max(0, videoVolume - 0.1))
          break
        case 'm':
          e.preventDefault()
          videoToggleMute()
          break
        case 'f':
          e.preventDefault()
          toggleFullscreen()
          break
        case 'Escape':
          e.preventDefault()
          setIsExpanded(false)
          break
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isExpanded, isVideoMode, videoIsPlaying, videoVolume, videoPause, videoResume, videoSetVolume, videoToggleMute])

  // Hide controls after inactivity in expanded mode
  const resetControlsTimeout = useCallback(() => {
    setShowControls(true)
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current)
    }
    if (isExpanded && videoIsPlaying) {
      controlsTimeoutRef.current = window.setTimeout(() => {
        setShowControls(false)
      }, 3000)
    }
  }, [isExpanded, videoIsPlaying])

  useEffect(() => {
    if (isExpanded) {
      resetControlsTimeout()
    }
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current)
      }
    }
  }, [isExpanded, videoIsPlaying, resetControlsTimeout])

  const seekToPercent = useCallback((percent: number) => {
    const safePercent = Math.max(0, Math.min(100, percent))
    if (remotePlayerId) {
      const remoteDuration = remoteStatus?.status.duration_seconds || duration
      if (remoteDuration <= 0) return
      const seconds = (safePercent / 100) * remoteDuration
      setSeekPreviewPercent(safePercent)
      if (remoteSeekTimerRef.current) window.clearTimeout(remoteSeekTimerRef.current)
      if (remoteSeekSettlingRef.current) window.clearTimeout(remoteSeekSettlingRef.current)
      remoteSeekTimerRef.current = window.setTimeout(() => {
        remotePlayers.command(remotePlayerId, { kind: 'seek', position_seconds: seconds })
          .then(() => refreshRemoteStatus())
          .catch(console.error)
        remoteSeekSettlingRef.current = window.setTimeout(() => {
          setSeekPreviewPercent(null)
          refreshRemoteStatus().catch(() => {})
        }, 2500)
      }, 120)
      return
    }
    if (isVideoMode) {
      // For transcoded video streams, we need to reload with a new start time
      const video = videoRef.current
      if (video && currentVideo) {
        const metadataDuration = currentVideo.durationMs ? currentVideo.durationMs / 1000 : 0
        const effectiveDuration = metadataDuration > 0 ? metadataDuration : videoDuration
        if (effectiveDuration > 0) {
          const newTime = (safePercent / 100) * effectiveDuration
          // Build new URL with start and quality parameters for server-side seeking
          const baseUrl = currentVideo.streamUrl.split('&start=')[0].split('&quality=')[0]
          const newUrl = `${baseUrl}&quality=${videoQuality}&start=${newTime}`
          video.src = newUrl
          video.play().catch(console.error)
          updateProgress(newTime, effectiveDuration, 0)
        }
      }
    } else {
      audioSeek(safePercent)
    }
  }, [remotePlayerId, remoteStatus?.status.duration_seconds, duration, refreshRemoteStatus, isVideoMode, currentVideo, videoDuration, videoQuality, updateProgress, audioSeek])

  const percentFromClientX = (element: HTMLDivElement, clientX: number) => {
    const rect = element.getBoundingClientRect()
    return Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100))
  }

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!canSeek) return
    seekToPercent(percentFromClientX(e.currentTarget, e.clientX))
  }

  const handleSeekPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!canSeek) return
    e.currentTarget.setPointerCapture(e.pointerId)
    seekToPercent(percentFromClientX(e.currentTarget, e.clientX))
  }

  const handleSeekPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!canSeek || e.buttons !== 1) return
    seekToPercent(percentFromClientX(e.currentTarget, e.clientX))
  }

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const vol = parseFloat(e.target.value)
    if (remotePlayerId) {
      setVolumePreview(vol)
      audioSetVolume(vol)
      if (remoteVolumeTimerRef.current) {
        window.clearTimeout(remoteVolumeTimerRef.current)
      }
      remoteVolumeTimerRef.current = window.setTimeout(() => {
        remotePlayers.command(remotePlayerId, { kind: 'volume', volume: vol })
          .then(() => {
            if (remoteVolumeSettlingRef.current) {
              window.clearTimeout(remoteVolumeSettlingRef.current)
            }
            remoteVolumeSettlingRef.current = window.setTimeout(() => {
              refreshRemoteStatus().finally(() => setVolumePreview(null)).catch(console.error)
            }, 4500)
          })
          .catch(console.error)
      }, 120)
      return
    }
    if (isVideoMode) {
      videoSetVolume(vol)
    } else {
      audioSetVolume(vol)
    }
  }

  const handlePlayPause = () => {
    if (remotePlayerId) {
      const remotePaused = effectiveRemotePaused ?? !isPlaying
      const nextPaused = !remotePaused
      setRemotePausedPreview(nextPaused)
      usePlayerStore.setState({
        isPlaying: !nextPaused,
        remoteStatus: remoteStatus ? { ...remoteStatus, status: { ...remoteStatus.status, paused: nextPaused } } : remoteStatus,
      })
      if (remotePausePreviewTimerRef.current) window.clearTimeout(remotePausePreviewTimerRef.current)
      remotePausePreviewTimerRef.current = window.setTimeout(() => setRemotePausedPreview(null), 4000)
      remotePlayers.command(remotePlayerId, { kind: nextPaused ? 'pause' : 'resume' })
        .catch(console.error)
      return
    }
    if (isVideoMode) {
      if (videoIsPlaying) {
        videoPause()
      } else {
        videoResume()
      }
    } else {
      if (audioIsPlaying) {
        audioPause()
      } else {
        audioResume()
      }
    }
  }

  const handleVideoClick = () => {
    if (isExpanded) {
      // In expanded mode, click toggles play/pause
      handlePlayPause()
    } else {
      // In collapsed mode, click expands
      setIsExpanded(true)
    }
  }

  const toggleFullscreen = () => {
    const container = containerRef.current
    if (!container) return

    if (!document.fullscreenElement) {
      container.requestFullscreen()
    } else {
      document.exitFullscreen()
    }
  }

  const skipForward = () => {
    const video = videoRef.current
    if (video && currentVideo) {
      const metadataDuration = currentVideo.durationMs ? currentVideo.durationMs / 1000 : 0
      const effectiveDuration = metadataDuration > 0 ? metadataDuration : videoDuration
      const newTime = Math.min(videoCurrentTime + 10, effectiveDuration)
      const baseUrl = currentVideo.streamUrl.split('&start=')[0].split('&quality=')[0]
      const newUrl = `${baseUrl}&quality=${videoQuality}&start=${newTime}`
      video.src = newUrl
      video.play().catch(console.error)
      updateProgress(newTime, effectiveDuration, 0)
    }
  }

  const skipBackward = () => {
    const video = videoRef.current
    if (video && currentVideo) {
      const metadataDuration = currentVideo.durationMs ? currentVideo.durationMs / 1000 : 0
      const effectiveDuration = metadataDuration > 0 ? metadataDuration : videoDuration
      const newTime = Math.max(videoCurrentTime - 10, 0)
      const baseUrl = currentVideo.streamUrl.split('&start=')[0].split('&quality=')[0]
      const newUrl = `${baseUrl}&quality=${videoQuality}&start=${newTime}`
      video.src = newUrl
      video.play().catch(console.error)
      updateProgress(newTime, effectiveDuration, 0)
    }
  }

  // Nothing playing
  if (!currentTrack && !currentVideo) {
    return (
      <div className="fixed inset-x-0 bottom-0 z-50 h-16 sm:h-20 bg-dark-950 border-t border-dark-800 flex items-center justify-center lg:static lg:inset-auto">
        <p className="text-dark-500 text-sm sm:text-base">No media playing</p>
      </div>
    )
  }

  // Get display info based on mode
  const title = isVideoMode
    ? currentVideo?.title
    : currentTrack?.title
  const subtitle = isVideoMode
    ? (currentVideo?.seriesTitle || (currentVideo?.mediaType === 'youtube' ? 'YouTube' : 'Video'))
    : ('artist_name' in (currentTrack || {}) ? (currentTrack as any).artist_name : null) || 'Unknown Artist'
  const coverPath = !isVideoMode && currentTrack && 'cover_path' in currentTrack ? (currentTrack as any).cover_path : null

  // Get upcoming tracks (after current)
  const upcomingTracks = queue.slice(queueIndex + 1)

  // Buffer progress for video
  const bufferPercent = isVideoMode && videoDuration > 0 ? (videoBuffered / videoDuration) * 100 : 0

  return (
    <>
      {/* Expanded fullscreen video overlay - controls only, video is behind at z-[99] */}
      {isExpanded && isVideoMode && (
        <div
          ref={containerRef}
          className="fixed inset-0 z-[100]"
          onMouseMove={resetControlsTimeout}
        >
          {/* Controls overlay */}
          <div
            className={`absolute inset-0 flex flex-col transition-opacity duration-300 pointer-events-none z-10 ${
              showControls ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {/* Top bar */}
            <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/80 to-transparent p-4 sm:p-6 pointer-events-auto">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setIsExpanded(false)}
                  className="p-2 text-white hover:text-primary-400 transition-colors"
                >
                  <ArrowLeft size={24} />
                </button>
                <div className="flex-1">
                  <h1 className="text-lg sm:text-xl font-semibold text-white">{title}</h1>
                  <p className="text-sm text-dark-300">{subtitle}</p>
                </div>
                <button
                  onClick={() => setIsExpanded(false)}
                  className="p-2 text-white hover:text-primary-400 transition-colors"
                  title="Minimize"
                >
                  <Minimize2 size={24} />
                </button>
              </div>
            </div>

            {/* Center play button */}
            {!videoIsPlaying && (
              <button
                onClick={() => videoResume()}
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 sm:w-24 sm:h-24 bg-primary-500/90 rounded-full flex items-center justify-center hover:bg-primary-400 transition-colors pointer-events-auto"
              >
                <Play size={40} className="sm:w-12 sm:h-12 text-white ml-1" />
              </button>
            )}

            {/* Bottom controls */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 sm:p-6 pointer-events-auto">
              {/* Progress bar */}
              <div
                className={`h-1.5 sm:h-2 bg-dark-600 rounded-full mb-4 group relative ${canSeek ? 'cursor-pointer' : 'cursor-default opacity-80'}`}
                onClick={canSeek ? handleSeek : undefined}
              >
                {/* Buffer progress */}
                <div
                  className="absolute h-full bg-dark-400 rounded-full pointer-events-none"
                  style={{ width: `${bufferPercent}%` }}
                />
                {/* Playback progress */}
                <div
                  className="absolute top-1/2 h-1 -translate-y-1/2 bg-primary-500 rounded-full pointer-events-none"
                  style={{ width: `${progress}%` }}
                >
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg" />
                </div>
              </div>

              {/* Control buttons */}
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 sm:gap-4">
                  <button
                    onClick={handlePlayPause}
                    className="p-2 text-white hover:text-primary-400 transition-colors"
                  >
                    {videoIsPlaying ? <Pause size={28} /> : <Play size={28} />}
                  </button>
                  <button
                    onClick={skipBackward}
                    className="p-2 text-white hover:text-primary-400 transition-colors"
                    title="Skip back 10s"
                  >
                    <SkipBack size={24} />
                  </button>
                  <button
                    onClick={skipForward}
                    className="p-2 text-white hover:text-primary-400 transition-colors"
                    title="Skip forward 10s"
                  >
                    <SkipForward size={24} />
                  </button>

                  {/* Time display */}
                  <span className="text-sm sm:text-base text-white ml-2">
                    {formatTime(videoCurrentTime)} / {formatTime(videoDuration)}
                  </span>
                </div>

                <div className="flex items-center gap-2 sm:gap-4">
                  {/* Volume controls */}
                  <div className="hidden sm:flex items-center gap-2">
                    <button
                      onClick={videoToggleMute}
                      className="p-2 text-white hover:text-primary-400 transition-colors"
                    >
                      {videoIsMuted || videoVolume === 0 ? <VolumeX size={24} /> : <Volume2 size={24} />}
                    </button>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={videoIsMuted ? 0 : videoVolume}
                      onChange={handleVolumeChange}
                      className="w-24 h-1 bg-dark-600 rounded-full appearance-none cursor-pointer
                               [&::-webkit-slider-thumb]:appearance-none
                               [&::-webkit-slider-thumb]:w-3
                               [&::-webkit-slider-thumb]:h-3
                               [&::-webkit-slider-thumb]:bg-white
                               [&::-webkit-slider-thumb]:rounded-full"
                    />
                  </div>

                  {/* Quality settings button */}
                  <div className="relative">
                    <button
                      onClick={() => setShowVideoQuality(!showVideoQuality)}
                      className="p-2 text-white hover:text-primary-400 transition-colors"
                      title="Quality settings"
                    >
                      <Settings size={24} />
                    </button>
                    {showVideoQuality && (
                      <div className="absolute bottom-full right-0 mb-2 bg-dark-800 rounded-lg shadow-xl border border-dark-600 py-2 min-w-[160px]">
                        <div className="px-3 py-1 text-xs text-dark-400 uppercase">Quality</div>
                        {QUALITY_OPTIONS.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => {
                              setVideoQuality(option.value)
                              setShowVideoQuality(false)
                            }}
                            className={`w-full px-3 py-2 text-left flex items-center justify-between hover:bg-dark-700 transition-colors ${
                              videoQuality === option.value ? 'text-primary-400' : 'text-white'
                            }`}
                          >
                            <span>{option.label}</span>
                            <span className="text-xs text-dark-400">{option.description}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Close button */}
                  <button
                    onClick={() => {
                      stopVideo()
                      setIsExpanded(false)
                    }}
                    className="p-2 text-white hover:text-red-400 transition-colors"
                    title="Stop video"
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile audio drawer */}
      {isAudioDrawerOpen && !isVideoMode && currentTrack && (
        <div className="fixed inset-0 z-[70] bg-dark-950 lg:hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-primary-900/30 via-dark-950 to-dark-950" />
          <div className="relative flex h-full flex-col overflow-y-auto px-5 pb-[calc(env(safe-area-inset-bottom)+1.25rem)] pt-[calc(env(safe-area-inset-top)+1rem)]">
            <div className="mb-4 flex items-center justify-between">
              <button onClick={() => setIsAudioDrawerOpen(false)} className="-ml-2 flex items-center gap-3 rounded-full px-3 py-2 text-dark-200 transition-colors hover:bg-dark-800 hover:text-white" aria-label="Collapse player">
                <ChevronDown size={28} />
                <span className="text-lg font-semibold">Now playing</span>
              </button>
              <div className="flex items-center gap-2">
                <button onClick={() => setShowLyrics(!showLyrics)} className={`rounded-full p-3 transition-colors ${showLyrics ? 'bg-primary-500 text-white' : 'bg-dark-800 text-dark-200'}`} title="Lyrics"><Mic2 size={22} /></button>
                <button onClick={() => setShowQueue(!showQueue)} className={`rounded-full p-3 transition-colors ${showQueue ? 'bg-primary-500 text-white' : 'bg-dark-800 text-dark-200'}`} title="View queue"><ListMusic size={22} /></button>
                <button onClick={() => setShowSettings(!showSettings)} className="rounded-full bg-dark-800 p-3 text-dark-200 transition-colors hover:text-white" title="Playback settings"><Settings size={22} /></button>
              </div>
            </div>
            <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-6 py-4">
              <div className="aspect-square w-full max-w-[72vw] overflow-hidden rounded-3xl bg-dark-800 shadow-2xl shadow-black/50 flex items-center justify-center">
                {getImageUrl(coverPath) ? <img src={getImageUrl(coverPath)!} alt="" className="h-full w-full object-cover" /> : <Disc3 size={72} className="text-dark-500" />}
              </div>
              <div className="w-full text-center">
                {'album_id' in currentTrack && currentTrack.album_id ? <Link to={`/albums/${currentTrack.album_id}`} className="block truncate text-3xl font-bold text-white hover:text-primary-300" onClick={() => setIsAudioDrawerOpen(false)}>{title}</Link> : <h2 className="truncate text-3xl font-bold text-white">{title}</h2>}
                {'artist_id' in currentTrack && currentTrack.artist_id ? <Link to={`/artists/${currentTrack.artist_id}`} className="mt-2 block truncate text-xl text-dark-300 hover:text-primary-300" onClick={() => setIsAudioDrawerOpen(false)}>{subtitle}</Link> : <p className="mt-2 truncate text-xl text-dark-300">{subtitle}</p>}
              </div>
              <div className="w-full space-y-3">
                <div className={`h-2 rounded-full bg-dark-800 relative overflow-hidden ${canSeek ? 'cursor-pointer' : 'cursor-default opacity-80'}`} onClick={canSeek ? handleSeek : undefined}><div className="absolute inset-y-0 left-0 rounded-full bg-primary-500" style={{ width: `${progress}%` }} /></div>
                <div className="flex justify-between text-sm text-dark-300"><span>{formatTime((progress / 100) * duration)}</span><span>{formatTime(duration)}</span></div>
              </div>
              <div className="flex w-full items-center justify-between text-dark-200">
                <button onClick={toggleShuffle} className={`rounded-full p-3 transition-colors ${shuffleEnabled ? 'text-primary-400' : 'hover:text-white'}`} title="Shuffle"><Shuffle size={30} /></button>
                <button onClick={previous} className="rounded-full p-3 transition-colors hover:text-white"><SkipBack size={34} /></button>
                <button onClick={handlePlayPause} className="flex h-20 w-20 items-center justify-center rounded-full bg-white text-dark-950 shadow-xl transition-transform active:scale-95">{isPlaying ? <Pause size={36} /> : <Play size={36} className="ml-1" />}</button>
                <button onClick={next} className="rounded-full p-3 transition-colors hover:text-white"><SkipForward size={34} /></button>
                <button onClick={cycleRepeatMode} className={`rounded-full p-3 transition-colors ${repeatMode !== 'off' ? 'text-primary-400' : 'hover:text-white'}`} title={repeatTitle} aria-label={`${repeatTitle}. Tap to cycle repeat mode`}><RepeatIcon size={30} /></button>
              </div>
              <div className="flex w-full items-center justify-center gap-5 text-dark-300">
                <button onClick={() => setRatingMutation.mutate(true)} disabled={setRatingMutation.isPending} className={`rounded-full p-4 transition-colors ${isLiked ? 'bg-primary-500/20 text-primary-300' : 'bg-dark-800 hover:text-white'}`} title={isLiked ? 'Remove thumbs up' : 'Thumbs up'}><ThumbsUp size={26} fill={isLiked ? 'currentColor' : 'none'} /></button>
                <button onClick={() => setRatingMutation.mutate(false)} disabled={setRatingMutation.isPending} className={`rounded-full p-4 transition-colors ${isDisliked ? 'bg-red-500/20 text-red-300' : 'bg-dark-800 hover:text-white'}`} title={isDisliked ? 'Remove thumbs down' : 'Thumbs down'}><ThumbsDown size={26} fill={isDisliked ? 'currentColor' : 'none'} /></button>
              </div>
              {showLyrics && (
                <div className="h-[42vh] w-full overflow-hidden rounded-3xl border border-dark-800 bg-dark-900/80 text-left shadow-2xl shadow-black/30">
                  <LyricsPanel isOpen={showLyrics} onClose={() => setShowLyrics(false)} embedded />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Queue Slide-out Panel */}
      {showQueue && !isVideoMode && !isAudioDrawerOpen && (
        <div className="fixed inset-0 z-40" onClick={() => setShowQueue(false)}>
          <div
            className="absolute right-0 top-0 bottom-16 sm:bottom-20 w-full sm:w-96 bg-dark-900 border-l border-dark-700 shadow-xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-dark-700">
              <h2 className="text-lg font-semibold">Queue</h2>
              <div className="flex items-center gap-2">
                {queue.length > 0 && (
                  <button
                    onClick={clearQueue}
                    className="p-2 text-dark-400 hover:text-red-400 transition-colors"
                    title="Clear queue"
                  >
                    <Trash2 size={18} />
                  </button>
                )}
                <button
                  onClick={() => setShowQueue(false)}
                  className="p-2 text-dark-400 hover:text-dark-200 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Queue Content */}
            <div className="flex-1 overflow-y-auto">
              {/* Now Playing */}
              {currentTrack && (
                <div className="p-4 border-b border-dark-700">
                  <p className="text-xs text-dark-500 uppercase tracking-wider mb-2">Now Playing</p>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-dark-700 rounded flex-shrink-0 overflow-hidden flex items-center justify-center">
                      {getImageUrl(coverPath) ? (
                        <img
                          src={getImageUrl(coverPath)!}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Disc3 size={20} className="text-dark-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-primary-400 truncate">{title}</p>
                      <p className="text-sm text-dark-400 truncate">{subtitle}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Up Next */}
              <div className="p-4">
                <p className="text-xs text-dark-500 uppercase tracking-wider mb-2">
                  Up Next ({upcomingTracks.length} tracks)
                </p>
                {upcomingTracks.length > 0 ? (
                  <div className="space-y-1">
                    {upcomingTracks.map((track, index) => {
                      const actualIndex = queueIndex + 1 + index
                      const trackCover = 'cover_path' in track ? track.cover_path : null
                      const isDragOver = dragOverQueueIndex === actualIndex
                      return (
                        <div
                          key={`${track.id}-${actualIndex}`}
                          draggable
                          onDragStart={(e) => {
                            setDraggedQueueIndex(actualIndex)
                            e.dataTransfer.effectAllowed = 'move'
                            e.dataTransfer.setData('text/plain', actualIndex.toString())
                            const target = e.target as HTMLElement
                            setTimeout(() => {
                              target.style.opacity = '0.5'
                            }, 0)
                          }}
                          onDragEnd={(e) => {
                            const target = e.target as HTMLElement
                            target.style.opacity = '1'
                            setDraggedQueueIndex(null)
                            setDragOverQueueIndex(null)
                          }}
                          onDragOver={(e) => {
                            e.preventDefault()
                            e.dataTransfer.dropEffect = 'move'
                            if (draggedQueueIndex !== null && actualIndex !== draggedQueueIndex) {
                              setDragOverQueueIndex(actualIndex)
                            }
                          }}
                          onDragLeave={() => {
                            setDragOverQueueIndex(null)
                          }}
                          onDrop={(e) => {
                            e.preventDefault()
                            if (draggedQueueIndex !== null && draggedQueueIndex !== actualIndex) {
                              reorderQueue(draggedQueueIndex, actualIndex)
                            }
                            setDraggedQueueIndex(null)
                            setDragOverQueueIndex(null)
                          }}
                          className={`flex items-center gap-2 p-2 rounded-lg hover:bg-dark-700 group cursor-pointer transition-colors ${
                            isDragOver ? 'border-t-2 border-primary-500 bg-dark-700/50' : ''
                          }`}
                          onClick={() => playFromQueue(actualIndex)}
                        >
                          <div
                            className="cursor-grab active:cursor-grabbing text-dark-500 hover:text-dark-300 transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <GripVertical size={16} />
                          </div>
                          <div className="w-10 h-10 bg-dark-600 rounded flex-shrink-0 overflow-hidden flex items-center justify-center">
                            {getImageUrl(trackCover) ? (
                              <img
                                src={getImageUrl(trackCover)!}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <Disc3 size={16} className="text-dark-500" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate group-hover:text-primary-400">
                              {track.title}
                            </p>
                            <p className="text-xs text-dark-400 truncate">
                              {'artist_name' in track ? track.artist_name : 'Unknown Artist'}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-dark-500">
                              {formatDuration(track.duration_ms)}
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                removeFromQueue(actualIndex)
                              }}
                              className="p-1 text-dark-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Remove from queue"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <p className="text-dark-500 text-sm">No upcoming tracks</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settings Slide-out Panel */}
      {showSettings && (
        <div className="fixed inset-0 z-40" onClick={() => setShowSettings(false)}>
          <div
            className="absolute right-0 top-0 bottom-16 sm:bottom-20 w-full sm:w-80 bg-dark-900 border-l border-dark-700 shadow-xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-dark-700">
              <h2 className="text-lg font-semibold">Playback Settings</h2>
              <button
                onClick={() => setShowSettings(false)}
                className="p-2 text-dark-400 hover:text-dark-200 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Settings Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Skip Downvoted Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Skip Downvoted</p>
                  <p className="text-sm text-dark-400">Auto-skip tracks you've disliked</p>
                </div>
                <button
                  onClick={() => setSkipDownvoted(!skipDownvoted)}
                  className={`w-12 h-6 rounded-full transition-colors relative ${
                    skipDownvoted ? 'bg-primary-500' : 'bg-dark-600'
                  }`}
                >
                  <div
                    className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      skipDownvoted ? 'translate-x-7' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* The single video element - position changes based on expanded state */}
      {isVideoMode && (
        <video
          ref={videoRef}
          className={`object-contain bg-black transition-all duration-300 ${
            isExpanded
              ? 'fixed inset-0 w-full h-full z-[99] cursor-pointer'
              : 'absolute bottom-0 left-0 h-16 sm:h-20 aspect-video z-[51] rounded cursor-pointer hover:ring-2 hover:ring-primary-500'
          }`}
          style={!isExpanded ? { marginLeft: '8px' } : undefined}
          playsInline
          disablePictureInPicture
          controlsList="nodownload noplaybackrate"
          onClick={handleVideoClick}
        />
      )}

      {/* Player Bar */}
      <div
        className={`fixed inset-x-0 bottom-0 z-50 h-16 sm:h-20 bg-dark-950 border-t border-dark-800 px-2 sm:px-4 items-center gap-2 sm:gap-4 lg:static lg:inset-auto ${isAudioDrawerOpen && !isVideoMode ? '!hidden lg:!flex' : ''} ${isExpanded && isVideoMode ? 'hidden' : 'flex'}`}
        {...(isExpanded && isVideoMode ? { inert: '' } : {}) as React.HTMLAttributes<HTMLDivElement>}
      >
        {/* Mobile expand handle */}
        {!isVideoMode && currentTrack && (
          <button onClick={() => setIsAudioDrawerOpen(true)} className="flex h-full w-10 flex-shrink-0 items-center justify-center border-r border-dark-800 text-dark-200 transition-colors hover:bg-dark-900 hover:text-white sm:hidden" aria-label="Expand player">
            <ChevronUp size={26} />
          </button>
        )}

        {/* Media info - compact on mobile */}
        <div className="flex items-center gap-2 sm:gap-3 w-auto sm:w-72 min-w-0">
          {/* Video placeholder or album art */}
          <button
            type="button"
            onClick={() => isVideoMode && setIsExpanded(true)}
            className={`${isVideoMode ? 'h-16 sm:h-20 aspect-video' : 'w-10 h-10 sm:w-12 sm:h-12'} bg-dark-700 rounded flex-shrink-0 overflow-hidden flex items-center justify-center ${isVideoMode ? 'cursor-pointer hover:ring-2 hover:ring-primary-500' : ''}`}
            aria-label={isVideoMode ? 'Open full player' : undefined}
          >
            {isVideoMode ? (
              // Placeholder - actual video is positioned over this box.
              currentVideo?.thumbnailPath ? (
                <img src={currentVideo.thumbnailPath} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-black" />
              )
            ) : getImageUrl(coverPath) ? (
              <img
                src={getImageUrl(coverPath)!}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              <Disc3 size={20} className="text-dark-500" />
            )}
          </button>
          {!isVideoMode && currentTrack && (
            <>
              <div className="min-w-0 hidden sm:block">
                {'album_id' in currentTrack && currentTrack.album_id ? (
                  <Link
                    to={`/albums/${currentTrack.album_id}`}
                    className="font-medium text-dark-100 truncate block hover:text-primary-400 hover:underline transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {title}
                  </Link>
                ) : (
                  <p className="font-medium text-dark-100 truncate">{title}</p>
                )}
                {'artist_id' in currentTrack && currentTrack.artist_id ? (
                  <Link
                    to={`/artists/${currentTrack.artist_id}`}
                    className="text-sm text-dark-400 truncate block hover:text-primary-400 hover:underline transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {subtitle}
                  </Link>
                ) : (
                  <p className="text-sm text-dark-400 truncate">{subtitle}</p>
                )}
              </div>
              {/* Like button */}
              <button
                onClick={() => likeMutation.mutate()}
                disabled={likeMutation.isPending}
                className={`p-2 transition-colors hidden sm:flex flex-shrink-0 ${
                  isLiked ? 'text-green-500 hover:text-green-400' : 'text-dark-400 hover:text-dark-200'
                }`}
                title={isLiked ? 'Unlike' : 'Like'}
              >
                <Heart size={20} fill={isLiked ? 'currentColor' : 'none'} />
              </button>
            </>
          )}
        </div>

        {/* Controls - centered and responsive */}
        <div className="flex-1 flex flex-col items-center gap-1 sm:gap-2">
          <div className="flex items-center gap-1 sm:gap-2">
            {/* Audio-only controls */}
            {!isVideoMode && (
              <>
                <button
                  onClick={toggleShuffle}
                  className={`p-1.5 sm:p-2 transition-colors ${
                    shuffleEnabled ? 'text-primary-400' : 'text-dark-400 hover:text-dark-100'
                  }`}
                  title="Shuffle"
                >
                  <Shuffle size={18} />
                </button>
                <button
                  onClick={previous}
                  className="p-1.5 sm:p-2 text-dark-400 hover:text-dark-100 transition-colors"
                >
                  <SkipBack size={18} className="sm:w-5 sm:h-5" />
                </button>
              </>
            )}
            <button
              onClick={handlePlayPause}
              className="p-2 sm:p-3 bg-white rounded-full text-dark-900 hover:scale-105 transition-transform"
            >
              {isPlaying ? <Pause size={18} className="sm:w-5 sm:h-5" /> : <Play size={18} className="sm:w-5 sm:h-5 ml-0.5" />}
            </button>
            {!isVideoMode && (
              <>
                <button
                  onClick={next}
                  className="p-1.5 sm:p-2 text-dark-400 hover:text-dark-100 transition-colors"
                >
                  <SkipForward size={18} className="sm:w-5 sm:h-5" />
                </button>
                <button
                  onClick={cycleRepeatMode}
                  className={`p-1.5 sm:p-2 transition-colors ${
                    repeatMode !== 'off' ? 'text-primary-400' : 'text-dark-400 hover:text-dark-100'
                  }`}
                  title={repeatTitle}
                  aria-label={`${repeatTitle}. Tap to cycle repeat mode`}
                >
                  <RepeatIcon size={18} />
                </button>
              </>
            )}
          </div>

          {/* Progress bar */}
          <div className="w-full max-w-xl flex items-center gap-2">
            <span className="text-xs text-dark-500 w-10 sm:w-12 text-right hidden sm:block">
              {isVideoMode ? formatTime(videoCurrentTime) : formatTime((progress / 100) * duration)}
            </span>
            <div
              className={`flex-1 h-3 -my-1 bg-dark-700 rounded-full group relative ${canSeek ? 'cursor-pointer touch-none' : 'cursor-default opacity-80'}`}
              onPointerDown={canSeek ? handleSeekPointerDown : undefined}
              onPointerMove={canSeek ? handleSeekPointerMove : undefined}
            >
              {/* Buffer progress (video only) */}
              {isVideoMode && (
                <div
                  className="absolute top-1/2 h-1 -translate-y-1/2 bg-dark-500 rounded-full pointer-events-none"
                  style={{ width: `${bufferPercent}%` }}
                />
              )}
              {/* Playback progress */}
              <div
                className="absolute h-full bg-primary-500 rounded-full pointer-events-none"
                style={{ width: `${progress}%` }}
              >
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
            <span className="text-xs text-dark-500 w-10 sm:w-12 hidden sm:block">{formatTime(duration)}</span>
          </div>
        </div>

        {/* Right controls */}
        <div className="flex items-center gap-1 sm:gap-4">
          {/* Keep lyrics desktop-only; mobile lyrics lives in the full-screen drawer. */}
          {!isVideoMode && (
            <>
              {/* Lyrics stays on desktop; mobile uses the full-screen player drawer. */}
              <button
                onClick={() => setShowLyrics(!showLyrics)}
                className={`hidden p-1.5 sm:block sm:p-2 transition-colors ${
                  showLyrics ? 'text-primary-400' : 'text-dark-400 hover:text-dark-100'
                }`}
                title="Lyrics"
              >
                <Mic2 size={18} className="sm:w-5 sm:h-5" />
              </button>

              {/* Queue button (audio only) */}
              <button
                onClick={() => setShowQueue(!showQueue)}
                className={`p-1.5 sm:p-2 transition-colors relative ${
                  showQueue ? 'text-primary-400' : 'text-dark-400 hover:text-dark-100'
                }`}
                title="View queue"
              >
                <ListMusic size={18} className="sm:w-5 sm:h-5" />
                {upcomingTracks.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary-500 rounded-full text-xs flex items-center justify-center">
                    {upcomingTracks.length > 9 ? '9+' : upcomingTracks.length}
                  </span>
                )}
              </button>
            </>
          )}

          {/* Cast button */}
          <div className="relative">
            <button
              onClick={() => setShowCast(!showCast)}
              className={`p-1.5 sm:p-2 transition-colors ${
                remotePlayerId ? 'text-primary-400' : 'text-dark-400 hover:text-dark-100'
              }`}
              title={remoteStatus ? `Playing on ${remoteStatus.name}` : 'Cast to player'}
            >
              <Cast size={18} className="sm:w-5 sm:h-5" />
            </button>
            {showCast && (
              <div className="absolute bottom-10 right-0 z-50 w-72 rounded-xl border border-dark-700 bg-dark-900 p-2 shadow-2xl">
                <button
                  onClick={() => { setRemotePlayer(null); setShowCast(false) }}
                  className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm ${!remotePlayerId ? 'bg-primary-500 text-white' : 'text-dark-100 hover:bg-dark-800'}`}
                >
                  <span>This browser</span>
                  {!remotePlayerId && <span>✓</span>}
                </button>
                {availableRemotePlayers.map((player) => (
                  <button
                    key={player.player_id}
                    disabled={!player.status.online}
                    onClick={() => { setRemotePlayer(player); setShowCast(false) }}
                    className={`mt-1 flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm disabled:opacity-40 ${remotePlayerId === player.player_id ? 'bg-primary-500 text-white' : 'text-dark-100 hover:bg-dark-800'}`}
                  >
                    <span className="min-w-0">
                      <span className="block truncate">{player.name}</span>
                      <span className="block text-xs opacity-70">{player.status.online ? (player.status.playing || 'Ready') : 'Offline'}</span>
                    </span>
                    {remotePlayerId === player.player_id && <span>✓</span>}
                  </button>
                ))}
                {availableRemotePlayers.length === 0 && <p className="px-3 py-2 text-xs text-dark-400">Register a Pi in its Settings to cast here.</p>}
              </div>
            )}
          </div>

          {/* Settings button */}
          <button
            onClick={() => setShowSettings(!showSettings)}
            className={`p-1.5 sm:p-2 transition-colors ${
              showSettings ? 'text-primary-400' : 'text-dark-400 hover:text-dark-100'
            }`}
            title="Playback settings"
          >
            <Settings size={18} className="sm:w-5 sm:h-5" />
          </button>

          {/* Volume control */}
          <div className="hidden sm:flex items-center gap-2 w-28 sm:w-32">
            <button
              onClick={isVideoMode ? videoToggleMute : audioToggleMute}
              className="text-dark-400 hover:text-dark-100 transition-colors"
            >
              {(isVideoMode ? videoIsMuted : audioIsMuted) || volume === 0 ? (
                <VolumeX size={20} />
              ) : (
                <Volume2 size={20} />
              )}
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={(isVideoMode ? videoIsMuted : audioIsMuted) ? 0 : volume}
              onChange={handleVolumeChange}
              className="w-full h-1 bg-dark-700 rounded-full appearance-none cursor-pointer
                       [&::-webkit-slider-thumb]:appearance-none
                       [&::-webkit-slider-thumb]:w-3
                       [&::-webkit-slider-thumb]:h-3
                       [&::-webkit-slider-thumb]:bg-white
                       [&::-webkit-slider-thumb]:rounded-full"
            />
          </div>

          {/* Close video button */}
          {isVideoMode && (
            <button
              onClick={stopVideo}
              className="p-1.5 sm:p-2 text-dark-400 hover:text-red-400 transition-colors"
              title="Stop video"
            >
              <X size={18} className="sm:w-5 sm:h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Lyrics Panel */}
      {!isVideoMode && <LyricsPanel isOpen={showLyrics} onClose={() => setShowLyrics(false)} />}
    </>
  )
}
